import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type Lang = "lv" | "en";

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Fallback so HMR boundary resets don't crash the app
const fallback: LanguageContextType = {
  lang: "lv",
  setLang: () => {},
  t: (key: string) => key,
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  return ctx ?? fallback;
};

/* ═══════════════════════════════════════════════
   TRANSLATIONS
   ═══════════════════════════════════════════════ */

const translations: Record<Lang, Record<string, string>> = {
  lv: {
    // Nav
    "nav.home": "Sākums",
    "nav.about": "Par mums",
    "nav.services": "Pakalpojumi",
    "nav.projects": "Projekti",
    "nav.contacts": "Kontakti",

    // Services dropdown
    "service.kondicionieri": "Kondicionieri",
    "service.ventilacija": "Ventilācija",
    "service.apkure": "Apkure",
    "service.projektesana": "Projektēšana",
    "service.serviss": "Serviss",
    "service.konsultacijas": "Konsultācijas",

    // Hero
    "hero.line1": "Būvējam ar",
    "hero.line2": "precizitāti",
    "hero.line3": "un uzticamību",
    "hero.desc": "Profesionālas apkures sistēmas, ventilācija, kondicionēšana un projektēšana Latvijā. Mēs piedāvājam energoefektīvus risinājumus būvniecībā ar augstāko kvalitāti un ilggadēju pieredzi.",
    "hero.cta": "Pieprasīt piedāvājumu",
    "hero.secondary": "Mūsu pakalpojumi",

    // Stats
    "stats.title": "Par mums",
    "stats.desc": "Ar vairāk nekā 20 gadu pieredzi inženiertehnisko sistēmu jomā, mēs garantējam uzticamību, kvalitāti un profesionālismu katrā projektā.",
    "stats.experience": "gadu pieredze",
    "stats.projects": "realizētu projektu",
    "stats.specialists": "kvalificēti speciālisti",
    "stats.satisfaction": "klientu apmierinātība",

    // Services section
    "services.title": "Mūsu pakalpojumi",
    "services.desc": "Piedāvājam pilnu inženiertehnisko risinājumu klāstu — no apkures un ventilācijas līdz projektēšanai un konsultācijām.",
    "services.apkure.desc": "Moderna apkures sistēmu projektēšana un uzstādīšana dzīvojamām un komerciālām ēkām.",
    "services.kondicionieri.desc": "Energoefektīvu kondicionēšanas iekārtu piegāde, montāža un apkalpošana.",
    "services.ventilacija.desc": "Ventilācijas sistēmu projektēšana, montāža un rekuperācijas risinājumi.",
    "services.projektesana.desc": "Pilns inženiertehnisko sistēmu projektēšanas cikls — no idejas līdz realizācijai.",
    "services.serviss.desc": "Regulāra apkope, remonts un garantijas serviss visām sistēmām.",
    "services.konsultacijas.desc": "Profesionālas konsultācijas par optimālākajiem inženiertehniskajiem risinājumiem.",

    // Projects section
    "projects.title": "Mūsu projekti",
    "projects.desc": "Iepazīstieties ar dažiem no mūsu nozīmīgākajiem realizētajiem projektiem.",
    "projects.view": "Skatīt projektu →",
    "projects.noImage": "Nav attēla",
    "projects.prev": "Iepriekšējais",
    "projects.next": "Nākamais",

    // Contact
    "contact.title": "Sazinies ar mums",
    "contact.name": "Vārds",
    "contact.phone": "Telefons",
    "contact.email": "E-pasts",
    "contact.message": "Ziņojums",
    "contact.send": "Nosūtīt",
    "contact.thanks": "Paldies! Mēs ar Jums sazināsimies.",

    // Footer
    "footer.requisites": "Rekvizīti",
    "footer.contacts": "Kontakti",
    "footer.address": "Adrese",
    "footer.phone": "Tālrunis",
    "footer.rights": "Visas tiesības aizsargātas.",

    // Service page layout
    "spl.back": "Uz sākumlapu",
    "spl.cta.title": "Nepieciešams individuāls risinājums?",
    "spl.cta.desc": "Sazinieties ar \"MJ-Partneri\", lai saņemtu profesionālu konsultāciju, tehnisko risinājumu un individuāli sagatavotu piedāvājumu tieši Jūsu objektam.",
    "spl.cta.quote": "Pieprasīt piedāvājumu",
    "spl.cta.services": "Skatīt pakalpojumus",

    // Back to top
    "backToTop": "Atpakaļ uz augšu",

    // Kontakti page
    "kontakti.pageTitle": "Sazinies ar mums",
    "kontakti.pageSubtitle": "Aizpildiet pieprasījuma formu vai sazinieties ar mums tieši — mēs atbildēsim pēc iespējas ātrāk.",
    "kontakti.selectedAC": "Izvēlētie kondicionieri",
    "kontakti.total": "Kopā",
    "kontakti.formTitle": "Pieprasījuma forma",
    "kontakti.name": "Vārds, Uzvārds",
    "kontakti.phone": "Tālrunis",
    "kontakti.email": "E-pasts",
    "kontakti.selectService": "Izvēlieties pakalpojumu",
    "kontakti.messagePlaceholder": "Papildu ziņa (neobligāti)",
    "kontakti.submit": "Nosūtīt pieprasījumu",
    "kontakti.submitCart": "Nosūtīt pieprasījumu par kondicionieriem",
    "kontakti.contactInfo": "Kontakti",
    "kontakti.requisites": "Rekvizīti",
    "kontakti.success.title": "Paldies par pieprasījumu!",
    "kontakti.success.desc": "Mēs ar Jums sazināsimies pēc iespējas ātrāk.",
    "kontakti.success.home": "Atgriezties uz sākumlapu",
    "kontakti.err.name": "Lūdzu ievadiet vārdu",
    "kontakti.err.phone": "Lūdzu ievadiet tālruņa numuru",
    "kontakti.err.email": "Lūdzu ievadiet derīgu e-pasta adresi",
    "kontakti.err.service": "Lūdzu izvēlieties pakalpojumu",
    "kontakti.boardChairman": "Valdes priekšsēdētājs",
    "kond.addToCart": "Pievienot grozam",
    "kond.inCart": "Grozā",
    "kond.cart": "Grozs",

    // Projects page
    "projekti.title": "Mūsu projekti",
    "projekti.desc": "Iepazīstieties ar mūsu realizētajiem projektiem klimata tehnoloģiju jomā.",
    "projekti.loading": "Ielādē...",
    "projekti.empty": "Pagaidām nav pievienotu projektu.",
    "projekti.viewProject": "Skatīt projektu →",

    // Project detail
    "projektaLapa.notFound": "Projekts nav atrasts",
    "projektaLapa.back": "Atpakaļ uz sākumlapu",
    "projektaLapa.allProjects": "Visi projekti",

    // 404
    "notFound.title": "404",
    "notFound.desc": "Lapa nav atrasta",
    "notFound.home": "Atgriezties uz sākumlapu",

    // Par mums page
    "parMums.title": "Par mums",
    "parMums.subtitle": "SIA \"MJ-Partneri\" ir profesionāla inženierkomunikāciju uzņēmums Latvijā, kas nodrošina augstākās kvalitātes projektēšanu, montāžu un servisu jau vairāk nekā 20 gadus.",
    "parMums.metaTitle": "Inženierkomunikāciju projektēšana un montāža | MJ-Partneri",
    "parMums.metaDesc": "Ventilācija, apkure, kondicionēšana, ūdensapgāde un elektrība. Pilna cikla inženierkomunikāciju projektēšana, montāža un serviss visā Latvijā.",
    "parMums.directions.title": "Darbības virzieni",
    "parMums.directions.intro": "SIA \"MJ-Partneri\" specializējas inženierkomunikāciju sistēmu projektēšanā, uzstādīšanā un montāžā visā Latvijā. Mūsu galvenie darbības virzieni:",
    "parMums.directions.1": "Ventilācijas sistēmas projektēšana un montāža",
    "parMums.directions.2": "Gaisa kondicionēšanas un dzesēšanas sistēmas",
    "parMums.directions.3": "Apkures un siltumapgādes risinājumi",
    "parMums.directions.4": "Elektrības sistēmu izbūve",
    "parMums.directions.5": "Ūdensapgāde un kanalizācija",
    "parMums.directions.footer": "Piedāvājam pilna cikla pakalpojumus – no projektēšanas līdz nodošanai ekspluatācijā.",
    "parMums.reputation.title": "Reputācija",
    "parMums.reputation.intro": "Mūsu reputācijas pamats ir klientu uzticība un kvalitatīvi realizēti projekti. \"MJ-Partneri\" ir kļuvis par uzticamu partneri ventilācijas, apkures un kondicionēšanas sistēmu (HVAC) projektēšanā un montāžā.",
    "parMums.reputation.experienceIntro": "Mūsu pieredze ietver dažādu objektu realizāciju:",
    "parMums.reputation.1": "Pārtikas ražošanas uzņēmumi",
    "parMums.reputation.2": "Tirdzniecības centri",
    "parMums.reputation.3": "Biroju ēkas",
    "parMums.reputation.4": "Privātmājas un daudzdzīvokļu ēkas",
    "parMums.reputation.5": "Noliktavas un industriālie objekti",
    "parMums.reputation.brands": "Strādājam ar pasaulē atzītiem zīmoliem un iekārtām, tostarp:",
    "parMums.reputation.footer": "Katram projektam nodrošinām individuāli pielāgotus tehniskos risinājumus, garantējot kvalitāti un ilgtspēju.",
    "parMums.service.title": "Serviss",
    "parMums.service.intro": "Papildus projektēšanai un montāžai piedāvājam arī regulāru sistēmu apkopi un servisu, kas nodrošina:",
    "parMums.service.1": "Iekārtu efektivitātes palielināšanu",
    "parMums.service.2": "Savlaicīgu bojājumu diagnostiku un novēršanu",
    "parMums.service.3": "Ilgāku sistēmu kalpošanas laiku",
    "parMums.service.footer": "Mūsu speciālisti sniedz profesionālas konsultācijas par optimālākajiem risinājumiem, pielāgotiem Jūsu vajadzībām.",
    "parMums.mission.title": "Uzdevums",
    "parMums.mission.intro": "Mūsu mērķis ir nodrošināt komfortablu un energoefektīvu mikroklimatu jebkurā objektā. Pareizi izbūvētas inženierkomunikāciju sistēmas ir būtiskas:",
    "parMums.mission.1": "Darba efektivitātei",
    "parMums.mission.2": "Komfortam un labsajūtai",
    "parMums.mission.3": "Energoefektivitātei",
    "parMums.mission.footer": "\"MJ-Partneri\" komandā strādā vairāk nekā {bold}20 kvalificēti un pieredzējuši speciālisti{/bold}, kas garantē augstu darba kvalitāti katrā projektā.",

    // Apkure page
    "apkure.title": "Apkures risinājumi Latvijā",
    "apkure.subtitle": "Latvijas klimatā apkures sistēmas nav tikai ērtība, bet gan nepieciešamība: apkures sezona ilgst aptuveni 200 dienas, un vairāk nekā puse (58 %) valsts primārās enerģijas patēriņa tiek izlietota siltuma nodrošināšanai.",
    "apkure.metaTitle": "Apkures risinājumi Latvijā | MJ-Partneri",
    "apkure.metaDesc": "MJ-Partneri piedāvā mūsdienīgus apkures risinājumus – radiatorus, siltās grīdas, siltumsūkņus un apkures katlus. Projektēšana un uzstādīšana visā Latvijā.",
    "apkure.intro": "Eiropas pētījumi norāda, ka gandrīz puse no visu enerģijas patēriņa tiek patērēta apkurei un dzesēšanai, un apmēram 80 % mājsaimniecību enerģijas izmanto tieši šiem mērķiem. Tas liecina, ka apkures risinājumi būtiski ietekmē komfortu, izmaksas un vides nospiedumu.",
    "apkure.s1.title": "Ko piedāvā MJ-Partneri?",
    "apkure.s1.p1": "MJ‑Partneri specializējas mūsdienīgu apkures risinājumu projektēšanā un uzstādīšanā. Uzņēmuma klāstā ir pasaules vadošo ražotāju sildķermeņi – radiatori, konvektori un gaisa sildītāji – kā arī augstas kvalitātes apkures katli. Daudzi klienti izvēlas siltās grīdas, un MJ‑Partneri speciālisti šīs sistēmas ātri un profesionāli ierīko gan dzīvojamās telpās, gan vannas istabās, baseinos vai atpūtas zonās.",
    "apkure.s1.p2": "Piedāvājumā ir arī dažādu jaudu malkas un gāzes katli, kā arī gaisa apkures katli, kas piemēroti lielveikalu, ražotņu, sporta halles un noliktavu apsildei. Saskaņā ar vajadzībām apkures sistēmas var integrēt sienās, griestos vai zem grīdas, tādējādi ļaujot optimāli izmantot telpu un saglabāt estētisku izskatu.",
    "apkure.s2.title": "Individuāli pielāgoti risinājumi",
    "apkure.s2.p1": "Katrā ēkā ir atšķirīgas prasības attiecībā uz siltuma jaudu, siltuma zudumiem un komfortu. Tāpēc MJ‑Partneri izstrādā apkures risinājumus, balstoties uz konkrētā objekta platību, plānojumu un lietošanas intensitāti. Šī pieeja ļauj samazināt enerģijas zudumus, uzturēt vienmērīgu mikroklimatu un nodrošināt, ka sistēma kalpo ilgi.",
    "apkure.s2.p2": "Turklāt modernās vadības tehnoloģijas ļauj precīzi regulēt temperatūru atsevišķās telpās, programmēt sistēmu atbilstoši diennakts režīmam un sekot enerģijas patēriņam, kas ilgtermiņā samazina rēķinus.",
    "apkure.table.title": "Apkures risinājumu salīdzinājums",
    "apkure.table.desc": "Tabulā apkopoti izplatītākie apkures risinājumi un to pielietojums. Siltās grīdas ir iecienītas, jo tās nodrošina vienmērīgu temperatūru un augstu komforta līmeni.",
    "apkure.table.h.solution": "Risinājums",
    "apkure.table.h.use": "Pielietojums",
    "apkure.table.h.pros": "Priekšrocības",
    "apkure.table.h.fuel": "Kurināmais",
    "apkure.table.r1": "Radiatori un konvektori|Dzīvokļi, biroji|Ātra montāža, plašs izmēru un dizaina klāsts|Ūdens, elektrība",
    "apkure.table.r2": "Siltās grīdas|Privātmājas, SPA zonas|Vienmērīga siltuma sadale, paaugstina komfortu|Ūdens, elektrība",
    "apkure.table.r3": "Gaisa sildītāji|Sporta halles, noliktavas|Strauji uzsilda gaisu, piemēroti lielām telpām|Elektrība, gāze",
    "apkure.table.r4": "Biomasa un granulu katli|Lauku īpašumi, privātmājas|Izmanto vietējos kurināmos, ekonomiskas darbības izmaksas|Koksne, granulas",
    "apkure.table.r5": "Gāzes katli|Dzīvokļi, mazākas ēkas|Kompakti, relatīvi lēti, ātra uzsildīšana|Gāze",
    "apkure.table.r6": "Gaisa apkures katli|Rūpnīcas, lielveikali|Spēj apsildīt plašas telpas, vienkārša montāža|Elektrība, gāze",
    "apkure.table.r7": "Siltumsūkņi|Jaunbūves, renovācijas|Augsta efektivitāte, izmanto dabas enerģiju|Elektrība, ģeotermija",
    "apkure.s3.title": "Energoefektivitāte un kurināmo izvēle",
    "apkure.s3.p1": "Pareiza kurināmā un tehnoloģijas izvēle ir būtiska ekonomikas un vides apsvērumu dēļ. 2018. gadā 46,7 % centralizētās un vietējās siltumapgādes Latvijā tika saražoti no atjaunojamiem resursiem, un no tiem 93,5 % bija biomasa. Individuālā apkure galvenokārt balstās uz malku, kas veido gandrīz 80 % patēriņa.",
    "apkure.s3.p2": "Klasiskie gāzes katli šķiet lēti, taču jāņem vērā to apkalpošanas izmaksas un fakts, ka gāze ir fosilais kurināmais ar metāna emisijām. Savukārt modernizēta centralizētā siltumapgāde piedāvā zemākas tarifu izmaksas un augstāku drošību. Arī siltumsūkņi un saules kolektori kļūst arvien pieejamāki un spēj ievērojami samazināt fosilā kurināmā patēriņu.",
    "apkure.s4.title": "Ilgtspējīgas tendences un secinājumi",
    "apkure.s4.p1": "Latvija un Eiropa kopumā virzās uz klimata neitralitāti, tāpēc arvien lielāka uzmanība tiek pievērsta apkures sistēmām, kas izmanto vietējos resursus un samazina emisijas. Modernas katlumājas ar dūmgāzu attīrīšanu un siltuma rekuperāciju, kā arī atsevišķa patēriņa mērīšana spēj nodrošināt zemas izmaksas un augstu efektivitāti.",
    "apkure.s4.p2": "Tajā pašā laikā joprojām jāattīsta centralizētā siltumapgāde, jo tā, ja izmanto atjaunojamus resursus, var nodrošināt vislētāko siltumu un augstu drošību.",
    "apkure.img.s1": "Radiatori un apkures risinājumi",
    "apkure.img.s2": "Siltās grīdas uzstādīšana",
    "apkure.img.s3": "Apkures katlu telpa",
    "apkure.img.s4": "Siltumsūkņa ārējais bloks",

    // Projektesana page
    "projektesana.title": "Projektēšana",
    "projektesana.subtitle": "Augsti kvalificēti projektētāji, kas izstrādā dažāda veida un apjoma inženierkomunikāciju projektus atbilstoši Latvijas būvnormatīviem.",
    "projektesana.section.title": "Inženierkomunikāciju projektēšana",
    "projektesana.p1": "\"MJ-Partneri\" projektēšanas nodaļā strādā augsti kvalificēti un pieredzējuši projektētāji, kas izstrādā dažāda veida un apjoma inženierkomunikāciju projektus.",
    "projektesana.p2": "Ievērojot Latvijas būvnormatīvus, mēs nodrošinām efektīvus un mūsdienīgus tehniskos risinājumus, lai projekti atbilstu ugunsdrošības, sanitārajām un drošības prasībām. Mūsu tehniskais nodrošinājums un specializētās projektēšanas programmas ļauj izstrādāt augstas kvalitātes projektus un precīzus tehniskos risinājumus. \"MJ-Partneri\" projektētāji piemeklēs piemērotāko risinājumu un sniegs ieteikumus par iekārtu izvēli gan dzīvokļiem, privātmājām un birojiem, gan arī lielveikaliem, ražotnēm, noliktavām un citiem objektiem.",

    // Serviss page
    "serviss.title": "Serviss",
    "serviss.subtitle": "Visu uzstādīto iekārtu servisa apkalpošana — tehniskā apsekošana, regulēšana un remonts.",
    "serviss.section.title": "Servisa apkalpošana",
    "serviss.p1": "\"MJ-Partneri\" nodrošina visu uzstādīto iekārtu servisa apkalpošanu, tai skaitā tehnisko apsekošanu, regulēšanu un remontu. Tehniskās pārbaudes veicam gan pēc ražotāju ieteiktajiem grafikiem, gan pēc individuāli saskaņota apkopes plāna ar pasūtītāju.",
    "serviss.p2": "Savlaicīga iespējamo defektu noteikšana un novēršana ir būtiska, lai pagarinātu iekārtu kalpošanas laiku un nodrošinātu to precīzu, ekonomisku un kvalitatīvu darbību. Mūsu speciālisti sniedz arī konsultācijas par iekārtu ekspluatāciju un piemērotākajiem tehniskajiem risinājumiem atbilstoši Jūsu vajadzībām. Sazinieties ar mums, un mēs palīdzēsim atrast labāko risinājumu.",

    // Konsultacijas page
    "konsultacijas.title": "Konsultācijas",
    "konsultacijas.subtitle": "Profesionālas konsultācijas inženierkomunikāciju jomā — piemērotākie un efektīvākie tehniskie risinājumi Jūsu objektam.",
    "konsultacijas.section.title": "Profesionālas konsultācijas",
    "konsultacijas.p1": "\"MJ-Partneri\" piedāvā profesionālas konsultācijas inženierkomunikāciju jomā, palīdzot izvēlēties piemērotākos un efektīvākos tehniskos risinājumus. Mēs sniedzam ieteikumus par apkures, ventilācijas, kondicionēšanas un citu sistēmu izvēli, projektēšanu un uzstādīšanu, ņemot vērā objekta specifiku un klienta prasības.",
    "konsultacijas.p2": "Mūsu speciālisti palīdz optimizēt iekārtu izvēli, izmaksas un energoefektivitāti, kā arī sniedz konsultācijas par esošo sistēmu uzlabošanu vai modernizāciju. Nodrošinām individuālu pieeju katram projektam, lai panāktu drošu, ilgtspējīgu un ekonomiski pamatotu risinājumu. Sazinieties ar mums, lai saņemtu profesionālu konsultāciju.",

    // Kondicionieri page
    "kond.title": "Kondicionieri",
    "kond.subtitle": "Gaisa kondicionieru piegāde, montāža un konsultācijas dzīvokļiem, privātmājām un birojiem.",
    "kond.metaDesc": "Gaisa kondicionieru piegāde, montāža un konsultācijas. Split, multi split, dizaina un mobilie kondicionieri no LG un Kaysun.",
    "kond.benefit.cooling": "Dzesēšana un sildīšana",
    "kond.benefit.montage": "Montāža un konsultācijas",
    "kond.benefit.home": "Dzīvokļiem un privātmājām",
    "kond.benefit.individual": "Individuāls piedāvājums",
    "kond.catalog": "E-katalogs",
    "kond.modelsAvailable": "modeļi pieejami",
    "kond.modelAvailable": "modelis pieejams",
    "kond.filters": "Filtri",
    "kond.sort": "Kārtot:",
    "kond.sortPopular": "Populārākie",
    "kond.sortPriceAsc": "Cena: zemākā",
    "kond.sortPriceDesc": "Cena: augstākā",
    "kond.sortArea": "Pēc telpas platības",
    "kond.noModels": "Nav atbilstošu modeļu",
    "kond.clearFilters": "Notīrīt filtrus",
    "kond.clearAllFilters": "Notīrīt visus filtrus",
    "kond.helpTitle": "Nezināt, kuru modeli izvēlēties?",
    "kond.helpDesc": "Sazinieties ar mūsu speciālistiem, un palīdzēsim atrast ideālo kondicionieri tieši jūsu vajadzībām un budžetam.",
    "kond.requestQuote": "Pieprasīt piedāvājumu",
    "kond.getConsultation": "Saņemt konsultāciju",
    "kond.usefulInfo": "Noderīga informācija",
    "kond.montagePrice": "Montāža tiek aprēķināta atsevišķi",
    "kond.cooling": "Dzesēšana",
    "kond.heating": "Sildīšana",
    "kond.filterClass": "Klase",
    "kond.filterBrand": "Zīmols",
    "kond.filterArea": "Telpas platība",
    "kond.filterType": "Tips",
    "kond.filterFeatures": "Funkcijas",
    "kond.all": "Visi",
    "kond.area.upTo": "līdz",
    "kond.roomArea": "Telpas platība",
    "kond.mainFunctions": "Galvenās funkcijas",
    "kond.price": "Cena",

    // Kond categories
    "kond.cat.economy": "Ekonomiskā klase",
    "kond.cat.standard": "Standarta klase",
    "kond.cat.design": "Dizaina klase",
    "kond.cat.multi": "Multi split sistēmas",
    "kond.cat.portable": "Mobilie kondicionieri",

    // Kond chips
    "kond.chip.all": "Visi",
    "kond.chip.upTo25": "Līdz 25 m²",
    "kond.chip.upTo35": "Līdz 35 m²",
    "kond.chip.upTo50": "Līdz 50 m²",
    "kond.chip.design": "Dizaina",
    "kond.chip.portable": "Mobilie",
    "kond.chip.withWifi": "Ar Wi-Fi",
    "kond.chip.withHeating": "Ar sildīšanu",

    // Kond type labels
    "kond.type.split": "Split",
    "kond.type.multi": "Multi split",
    "kond.type.portable": "Mobilais",
    "kond.type.design": "Dizaina",

    // Kond feature labels
    "kond.feat.selfDiag": "Pašdiagnostika",
    "kond.feat.ionFilter": "Jonu filtrs",
    "kond.feat.heating": "Sildīšana",

    // Kond room size options
    "kond.room.upTo25": "līdz 25 m²",
    "kond.room.upTo35": "līdz 35 m²",
    "kond.room.upTo50": "līdz 50 m²",
    "kond.room.upTo70": "līdz 70 m²",

    // Kond accordion titles
    "kond.acc.apartment": "Kondicionieri dzīvoklim",
    "kond.acc.house": "Kondicionieri privātmājai",
    "kond.acc.commercial": "Kondicionieri sabiedriskām ēkām un birojiem",
    "kond.acc.ventilation": "Kondicionieri un ventilācija",
    "kond.acc.portable": "Portatīvie / mobilie kondicionieri",
    "kond.acc.lg": "LG gaisa kondicionieri",
    "kond.acc.montage": "Konsultācijas un montāža",

    // Kond accordion content
    "kond.acc.apartment.p": "Izvēloties gaisa kondicionieri dzīvoklim, svarīgi izvērtēt, kurā telpā karstums ir visizteiktākais, vai iespējams izvietot āra bloku uz fasādes, balkona vai lodžijas, kā arī kāds ir nepieciešamais trokšņa līmenis un jauda. Ja āra bloka uzstādīšana nav iespējama, risinājums var būt mobilais kondicionieris, taču ikdienas lietošanai parasti efektīvāki, klusāki un ērtāki ir sienas split tipa kondicionieri.",
    "kond.acc.house.p": "Privātmājām svarīgi izvērtēt ne tikai cenu, bet arī telpu platību, telpu skaitu, iekārtu izvietojumu un ekspluatācijas izmaksas. Vienai telpai bieži pietiek ar split sistēmu, savukārt vairākām telpām īpaši piemēroti ir multi split kondicionieri, kur vairāki iekšējie bloki tiek pieslēgti vienam ārējam blokam.",
    "kond.acc.commercial.p": "Birojiem, veikaliem, kafejnīcām un citām sabiedriskām telpām piedāvājam risinājumus, kas nodrošina ātru un stabilu temperatūras kontroli. Vairākām telpām īpaši piemērotas ir multi split sistēmas, kas palīdz samazināt āra bloku skaitu.",
    "kond.acc.ventilation.p": "Kondicionieris nodrošina gaisa dzesēšanu vai sildīšanu, bet ventilācijas sistēma nodrošina svaiga gaisa pieplūdi un gaisa apmaiņu telpās. Lai iegūtu pilnvērtīgu mikroklimatu, bieži labākais risinājums ir kondicionēšanas un ventilācijas sistēmu savienojums.",
    "kond.acc.portable.p": "Mobilie kondicionieri ir piemēroti situācijām, kad nav iespējams uzstādīt āra bloku vai nepieciešams pagaidu risinājums. Jārēķinās, ka šie modeļi parasti ir skaļāki un mazāk efektīvi par sienas split sistēmām.",
    "kond.acc.lg.p": "LG piedāvā plašu kondicionieru klāstu ar Wi-Fi vadību, A++ energoefektivitāti un modernu dizainu. Standarta Plus sērija nodrošina uzticamu ikdienas lietošanu, savukārt Artcool Mirror un Gallery sērijas izceļas ar unikālu vizuālo dizainu, kas kļūst par interjera elementu.",
    "kond.acc.montage.intro": "Pirms kondicioniera izvēles iesakām konsultēties ar mūsu speciālistiem. Mēs palīdzēsim izvērtēt:",
    "kond.acc.montage.1": "Telpas platību un plānojumu",
    "kond.acc.montage.2": "Uzstādīšanas iespējas",
    "kond.acc.montage.3": "Nepieciešamo jaudu",
    "kond.acc.montage.4": "Piemērotāko iekārtas tipu",
    "kond.acc.montage.5": "Budžetu un ekspluatācijas izmaksas",
    "kond.acc.montage.footer": "Montāžas cena tiek aprēķināta atsevišķi pēc objekta apsekošanas.",

    // Ventilacija page
    "vent.title": "Ventilācija",
    "vent.subtitle": "Profesionāla ventilācijas sistēmu projektēšana, montāža un serviss sabiedriskām, ražošanas un dzīvojamām ēkām visā Latvijā.",
    "vent.metaTitle": "Ventilācija | MJ-Partneri",
    "vent.metaDesc": "Ventilācijas sistēmu projektēšana un montāža sabiedriskām, ražošanas un dzīvojamām ēkām. Rekuperācija, mehāniskā ventilācija un serviss.",
    "vent.s1.title": "Ventilācija sabiedriskām ēkām",
    "vent.s1.p1": "Ventilācija sabiedriskās ēkās ir būtisks priekšnosacījums komfortam, veselībai un higiēnas prasību nodrošināšanai. Pieaugot būvniecības kvalitātes standartiem, palielinās arī nepieciešamība pēc efektīvām ventilācijas sistēmām, kas nodrošina kvalitatīvu gaisa apmaiņu.",
    "vent.s1.p2": "\"MJ-Partneri\" nodrošina ventilācijas sistēmu projektēšanu un montāžu sabiedriskām un ražošanas ēkām, ņemot vērā cilvēku skaitu, telpu funkciju un citus būtiskus parametrus. Mūsu risinājumi ļauj regulēt gaisa temperatūru, mitrumu un plūsmas ātrumu, nodrošinot optimālu mikroklimatu katrā telpā.",
    "vent.s2.title": "Ventilācija ražošanas ēkām",
    "vent.s2.p1": "Ventilācija ražošanas objektos ir īpaši svarīga, jo tajos rodas paaugstināts gaisa piesārņojums – dūmi, putekļi, tvaiki un citas kaitīgas daļiņas. \"MJ-Partneri\" ir pieredze ventilācijas sistēmu projektēšanā un izbūvē ražošanas ēkām, pielāgojot risinājumus konkrētajiem tehnoloģiskajiem procesiem.",
    "vent.s2.p2": "Mūsdienīgas ventilācijas sistēmas ar siltuma atgūšanu ļauj būtiski samazināt ekspluatācijas izmaksas un uzlabot energoefektivitāti. Esam realizējuši projektus rūpnīcās, kokrūpniecībā, pārtikas ražošanā, noliktavās un metināšanas cehos. Pareizi izbūvēta ventilācija nodrošina stabilu ražošanas procesu un novērš dīkstāves riskus.",
    "vent.s3.title": "Ventilācija privātmājām",
    "vent.s3.p1": "Mūsdienās arvien vairāk klientu izvēlas ventilāciju privātmājām, jo modernās, hermētiskās ēkas prasa kontrolētu gaisa apmaiņu. Pareizi projektēta ventilācijas sistēma nodrošina veselīgu mikroklimatu un energoefektivitāti.",
    "vent.s3.p2": "\"MJ-Partneri\" piedāvā individuālus risinājumus, izmantojot rekuperācijas sistēmas (siltummaiņus), kas nodrošina svaigā gaisa pieplūdi, vienlaikus samazinot siltuma zudumus. Pareizi izbūvēta ventilācija darbojas klusi un nerada caurvēju, nodrošinot komfortu visās telpās.",
    "vent.s4.title": "Ventilācija dzīvokļiem",
    "vent.s4.p1": "Daudzos dzīvokļos ventilācijas sistēmas ir novecojušas un nepietiekamas, kas veicina mitrumu, pelējumu un sliktu gaisa kvalitāti. Logu atvēršana nodrošina tikai īslaicīgu efektu un rada siltuma zudumus.",
    "vent.s4.p2": "Mūsdienīgi risinājumi ļauj uzstādīt kompaktas ventilācijas sistēmas dzīvokļiem, nodrošinot pastāvīgu svaigā gaisa pieplūdi un uzlabotu mikroklimatu. Šādas sistēmas neaizņem daudz vietas un efektīvi uzlabo dzīves kvalitāti.",
    "vent.s5.title": "Ventilācijas sistēmas",
    "vent.s5.p1": "Pareizi projektēta un uzstādīta ventilācijas sistēma ir viens no galvenajiem iekštelpu komforta faktoriem. Nepietiekama ventilācija izraisa paaugstinātu CO₂ līmeni, mitrumu un pelējuma veidošanos.",
    "vent.s5.p2": "\"MJ-Partneri\" piedāvā gan mehāniskās ventilācijas sistēmas, gan esošo sistēmu uzlabošanu. Mehāniskā ventilācija nodrošina kontrolētu gaisa apmaiņu, filtrē gaisu un uztur optimālu temperatūru un mitrumu visa gada garumā.",
    "vent.s5.p3": "Mēs veicam ventilācijas sistēmu projektēšanu, montāžu un servisu – no dzīvokļiem līdz lielām industriālām un tirdzniecības ēkām, nodrošinot katram objektam piemērotāko risinājumu.",
    "vent.s1.alt": "Ventilācija sabiedriskām ēkām",
    "vent.s2.alt": "Ventilācija ražošanas ēkām",
    "vent.s3.alt": "Ventilācija privātmājām",
    "vent.s4.alt": "Ventilācija dzīvokļiem",
    "vent.s5.alt": "Ventilācijas sistēmas",

    // Admin
    "admin.title": "Projektu pārvaldība",
    "admin.changePassword": "Mainīt paroli",
    "admin.toSite": "Mājaslapā",
    "admin.logout": "Izrakstīties",
    "admin.addProject": "Pievienot projektu",
    "admin.editProject": "Rediģēt projektu",
    "admin.newProject": "Jauns projekts",
    "admin.titleLabel": "Nosaukums *",
    "admin.titleEn": "Nosaukums (EN)",
    "admin.locationLabel": "Atrašanās vieta *",
    "admin.locationEn": "Atrašanās vieta (EN)",
    "admin.shortDesc": "Īss apraksts *",
    "admin.shortDescEn": "Īss apraksts (EN)",
    "admin.longDesc": "Pilns apraksts *",
    "admin.longDescEn": "Pilns apraksts (EN)",
    "admin.image": "Attēls",
    "admin.imageRequired": "Pievienojiet attēlu.",
    "admin.fillAll": "Aizpildiet visus laukus.",
    "admin.saved": "Saglabāts!",
    "admin.savedDesc": "Projekts veiksmīgi pievienots.",
    "admin.updated": "Atjaunināts!",
    "admin.updatedDesc": "Projekts veiksmīgi rediģēts.",
    "admin.deleted": "Dzēsts!",
    "admin.deletedDesc": "Projekts veiksmīgi noņemts.",
    "admin.error": "Kļūda",
    "admin.saveFailed": "Neizdevās saglabāt.",
    "admin.deleteFailed": "Neizdevās dzēst.",
    "admin.deleteConfirmTitle": "Dzēst projektu?",
    "admin.deleteConfirmDesc": "Vai tiešām vēlaties dzēst šo projektu? Šī darbība ir neatgriezeniska.",
    "admin.cancel": "Atcelt",
    "admin.delete": "Dzēst",
    "admin.save": "Saglabāt",
    "admin.saving": "Saglabā...",
    "admin.loading": "Ielādē...",
    "admin.newPassword": "Jaunā parole",
    "admin.confirmPassword": "Apstiprināt paroli",
    "admin.passwordMin": "Vismaz 6 rakstzīmes",
    "admin.passwordRepeat": "Atkārtojiet paroli",
    "admin.passwordShort": "Parolei jābūt vismaz 6 rakstzīmēm.",
    "admin.passwordMismatch": "Paroles nesakrīt.",
    "admin.passwordChanged": "Veiksmīgi!",
    "admin.passwordChangedDesc": "Parole nomainīta.",
    "admin.imageKeep": "(atstāt tukšu, lai saglabātu esošo)",
    "admin.oneImage": "Var augšupielādēt tikai vienu attēlu.",
  },

  en: {
    // Nav
    "nav.home": "Home",
    "nav.about": "About Us",
    "nav.services": "Services",
    "nav.projects": "Projects",
    "nav.contacts": "Contacts",

    // Services dropdown
    "service.kondicionieri": "Air Conditioners",
    "service.ventilacija": "Ventilation",
    "service.apkure": "Heating",
    "service.projektesana": "Engineering Design",
    "service.serviss": "Service",
    "service.konsultacijas": "Consultation",

    // Hero
    "hero.line1": "Building with",
    "hero.line2": "precision",
    "hero.line3": "and reliability",
    "hero.desc": "Professional heating systems, ventilation, air conditioning and engineering design in Latvia. We offer energy-efficient construction solutions with the highest quality and years of experience.",
    "hero.cta": "Request Quote",
    "hero.secondary": "Our Services",

    // Stats
    "stats.title": "About Us",
    "stats.desc": "With over 20 years of experience in engineering systems, we guarantee reliability, quality and professionalism in every project.",
    "stats.experience": "years of experience",
    "stats.projects": "completed projects",
    "stats.specialists": "qualified specialists",
    "stats.satisfaction": "client satisfaction",

    // Services section
    "services.title": "Our Services",
    "services.desc": "We offer a full range of engineering solutions — from heating and ventilation to design and consultation.",
    "services.apkure.desc": "Modern heating system design and installation for residential and commercial buildings.",
    "services.kondicionieri.desc": "Energy-efficient air conditioning supply, installation and maintenance.",
    "services.ventilacija.desc": "Ventilation system design, installation and heat recovery solutions.",
    "services.projektesana.desc": "Full engineering system design cycle — from concept to implementation.",
    "services.serviss.desc": "Regular maintenance, repair and warranty service for all systems.",
    "services.konsultacijas.desc": "Professional consultations on optimal engineering solutions.",

    // Projects section
    "projects.title": "Our Projects",
    "projects.desc": "Explore some of our most significant completed projects.",
    "projects.view": "View project →",
    "projects.noImage": "No image",
    "projects.prev": "Previous",
    "projects.next": "Next",

    // Contact
    "contact.title": "Contact Us",
    "contact.name": "Name",
    "contact.phone": "Phone",
    "contact.email": "Email",
    "contact.message": "Message",
    "contact.send": "Send",
    "contact.thanks": "Thank you! We will contact you soon.",

    // Footer
    "footer.requisites": "Company Details",
    "footer.contacts": "Contacts",
    "footer.address": "Address",
    "footer.phone": "Phone",
    "footer.rights": "All rights reserved.",

    // Service page layout
    "spl.back": "Back to Home",
    "spl.cta.title": "Need a custom solution?",
    "spl.cta.desc": "Contact MJ-Partneri for a professional consultation, technical solution and individually prepared proposal for your project.",
    "spl.cta.quote": "Request Quote",
    "spl.cta.services": "View Services",

    // Back to top
    "backToTop": "Back to top",

    // Kontakti page
    "kontakti.pageTitle": "Contact Us",
    "kontakti.pageSubtitle": "Fill out the inquiry form or contact us directly — we will respond as soon as possible.",
    "kontakti.selectedAC": "Selected air conditioners",
    "kontakti.total": "Total",
    "kontakti.formTitle": "Inquiry Form",
    "kontakti.name": "Full Name",
    "kontakti.phone": "Phone",
    "kontakti.email": "Email",
    "kontakti.selectService": "Select a service",
    "kontakti.messagePlaceholder": "Additional message (optional)",
    "kontakti.submit": "Send Inquiry",
    "kontakti.submitCart": "Send AC Inquiry",
    "kontakti.contactInfo": "Contact Information",
    "kontakti.requisites": "Company Details",
    "kontakti.success.title": "Thank you for your inquiry!",
    "kontakti.success.desc": "We will contact you as soon as possible.",
    "kontakti.success.home": "Return to Home",
    "kontakti.err.name": "Please enter your name",
    "kontakti.err.phone": "Please enter your phone number",
    "kontakti.err.email": "Please enter a valid email address",
    "kontakti.err.service": "Please select a service",
    "kontakti.boardChairman": "Board Chairman",
    "kond.addToCart": "Add to Cart",
    "kond.inCart": "In Cart",
    "kond.cart": "Cart",

    // Projects page
    "projekti.title": "Our Projects",
    "projekti.desc": "Explore our completed projects in climate technology.",
    "projekti.loading": "Loading...",
    "projekti.empty": "No projects added yet.",
    "projekti.viewProject": "View project →",

    // Project detail
    "projektaLapa.notFound": "Project not found",
    "projektaLapa.back": "Back to home",
    "projektaLapa.allProjects": "All projects",

    // 404
    "notFound.title": "404",
    "notFound.desc": "Page not found",
    "notFound.home": "Return to Home",

    // Par mums page
    "parMums.title": "About Us",
    "parMums.subtitle": "SIA \"MJ-Partneri\" is a professional engineering communications company in Latvia, providing the highest quality design, installation and service for over 20 years.",
    "parMums.metaTitle": "Engineering Communication Design and Installation | MJ-Partneri",
    "parMums.metaDesc": "Ventilation, heating, air conditioning, water supply and electrical systems. Full-cycle engineering communication design, installation and service throughout Latvia.",
    "parMums.directions.title": "Areas of Activity",
    "parMums.directions.intro": "SIA \"MJ-Partneri\" specializes in engineering communication system design, installation and assembly throughout Latvia. Our main areas of activity:",
    "parMums.directions.1": "Ventilation system design and installation",
    "parMums.directions.2": "Air conditioning and cooling systems",
    "parMums.directions.3": "Heating and heat supply solutions",
    "parMums.directions.4": "Electrical system construction",
    "parMums.directions.5": "Water supply and sewerage",
    "parMums.directions.footer": "We offer full-cycle services — from design to commissioning.",
    "parMums.reputation.title": "Reputation",
    "parMums.reputation.intro": "The foundation of our reputation is client trust and quality completed projects. MJ-Partneri has become a reliable partner in HVAC system design and installation.",
    "parMums.reputation.experienceIntro": "Our experience includes various facility implementations:",
    "parMums.reputation.1": "Food production companies",
    "parMums.reputation.2": "Shopping centers",
    "parMums.reputation.3": "Office buildings",
    "parMums.reputation.4": "Private homes and apartment buildings",
    "parMums.reputation.5": "Warehouses and industrial facilities",
    "parMums.reputation.brands": "We work with globally recognized brands and equipment, including:",
    "parMums.reputation.footer": "For each project, we provide individually customized technical solutions, guaranteeing quality and sustainability.",
    "parMums.service.title": "Service",
    "parMums.service.intro": "In addition to design and installation, we also offer regular system maintenance and service that ensures:",
    "parMums.service.1": "Increased equipment efficiency",
    "parMums.service.2": "Timely fault diagnosis and elimination",
    "parMums.service.3": "Longer system lifespan",
    "parMums.service.footer": "Our specialists provide professional consultations on optimal solutions tailored to your needs.",
    "parMums.mission.title": "Mission",
    "parMums.mission.intro": "Our goal is to ensure comfortable and energy-efficient indoor climate in any facility. Properly built engineering communication systems are essential for:",
    "parMums.mission.1": "Work efficiency",
    "parMums.mission.2": "Comfort and well-being",
    "parMums.mission.3": "Energy efficiency",
    "parMums.mission.footer": "The MJ-Partneri team includes more than {bold}20 qualified and experienced specialists{/bold}, guaranteeing high work quality in every project.",

    // Apkure page
    "apkure.title": "Heating Solutions in Latvia",
    "apkure.subtitle": "In Latvia's climate, heating systems are not just a convenience but a necessity: the heating season lasts approximately 200 days, and more than half (58%) of the country's primary energy consumption is used for heat supply.",
    "apkure.metaTitle": "Heating Solutions in Latvia | MJ-Partneri",
    "apkure.metaDesc": "MJ-Partneri offers modern heating solutions – radiators, underfloor heating, heat pumps and boilers. Design and installation across Latvia.",
    "apkure.intro": "European studies indicate that nearly half of all energy consumption is spent on heating and cooling, and approximately 80% of household energy is used for these purposes. This shows that heating solutions significantly affect comfort, costs and environmental footprint.",
    "apkure.s1.title": "What Does MJ-Partneri Offer?",
    "apkure.s1.p1": "MJ‑Partneri specialises in the design and installation of modern heating solutions. The company's range includes heating elements from world-leading manufacturers – radiators, convectors and air heaters – as well as high-quality boilers. Many clients choose underfloor heating, and MJ‑Partneri specialists quickly and professionally install these systems in living spaces, bathrooms, pools and recreation areas.",
    "apkure.s1.p2": "The range also includes various capacity wood and gas boilers, as well as air heating boilers suitable for supermarkets, factories, sports halls and warehouses. Heating systems can be integrated into walls, ceilings or under floors as needed, allowing optimal use of space while maintaining an aesthetic appearance.",
    "apkure.s2.title": "Individually Tailored Solutions",
    "apkure.s2.p1": "Each building has different requirements regarding heat output, heat losses and comfort. Therefore, MJ‑Partneri develops heating solutions based on the specific facility's area, layout and usage intensity. This approach reduces energy losses, maintains a uniform microclimate and ensures long system life.",
    "apkure.s2.p2": "Moreover, modern control technologies allow precise temperature regulation in individual rooms, programming the system according to daily schedules and monitoring energy consumption, which reduces bills in the long term.",
    "apkure.table.title": "Heating Solutions Comparison",
    "apkure.table.desc": "The table summarises the most common heating solutions and their applications. Underfloor heating is popular because it provides uniform temperature and a high level of comfort.",
    "apkure.table.h.solution": "Solution",
    "apkure.table.h.use": "Application",
    "apkure.table.h.pros": "Advantages",
    "apkure.table.h.fuel": "Fuel Type",
    "apkure.table.r1": "Radiators & convectors|Apartments, offices|Quick installation, wide range of sizes and designs|Water, electricity",
    "apkure.table.r2": "Underfloor heating|Private homes, SPA areas|Even heat distribution, increased comfort|Water, electricity",
    "apkure.table.r3": "Air heaters|Sports halls, warehouses|Rapidly heats air, suitable for large spaces|Electricity, gas",
    "apkure.table.r4": "Biomass & pellet boilers|Rural properties, private homes|Uses local fuels, economical operating costs|Wood, pellets",
    "apkure.table.r5": "Gas boilers|Apartments, smaller buildings|Compact, relatively inexpensive, fast heating|Gas",
    "apkure.table.r6": "Air heating boilers|Factories, supermarkets|Can heat large spaces, simple installation|Electricity, gas",
    "apkure.table.r7": "Heat pumps|New builds, renovations|High efficiency, uses natural energy|Electricity, geothermal",
    "apkure.s3.title": "Energy Efficiency & Fuel Choice",
    "apkure.s3.p1": "Choosing the right fuel and technology is essential for economic and environmental reasons. In 2018, 46.7% of centralised and local heat supply in Latvia was produced from renewable resources, of which 93.5% was biomass. Individual heating mainly relies on firewood, which accounts for nearly 80% of consumption.",
    "apkure.s3.p2": "Traditional gas boilers seem cheap, but maintenance costs and the fact that gas is a fossil fuel with methane emissions must be considered. Meanwhile, modernised district heating offers lower tariff costs and higher safety. Heat pumps and solar collectors are also becoming increasingly accessible and can significantly reduce fossil fuel consumption.",
    "apkure.s4.title": "Sustainable Trends & Conclusions",
    "apkure.s4.p1": "Latvia and Europe as a whole are moving towards climate neutrality, so increasing attention is being paid to heating systems that use local resources and reduce emissions. Modern boiler houses with flue gas cleaning and heat recovery, as well as individual consumption metering, can ensure low costs and high efficiency.",
    "apkure.s4.p2": "At the same time, district heating still needs to be developed, as it can provide the cheapest heat and high safety when using renewable resources.",
    "apkure.img.s1": "Radiators and heating solutions",
    "apkure.img.s2": "Underfloor heating installation",
    "apkure.img.s3": "Boiler room",
    "apkure.img.s4": "Heat pump outdoor unit",

    // Projektesana page
    "projektesana.title": "Engineering Design",
    "projektesana.subtitle": "Highly qualified designers who develop engineering communication projects of various types and scales in compliance with Latvian building regulations.",
    "projektesana.section.title": "Engineering Communication Design",
    "projektesana.p1": "The MJ-Partneri design department employs highly qualified and experienced designers who develop engineering communication projects of various types and scales.",
    "projektesana.p2": "Following Latvian building regulations, we provide effective and modern technical solutions to ensure projects comply with fire safety, sanitary and security requirements. Our technical capabilities and specialized design software allow us to develop high-quality projects and precise technical solutions. MJ-Partneri designers will select the most suitable solution and provide recommendations on equipment selection for apartments, private homes and offices, as well as supermarkets, factories, warehouses and other facilities.",

    // Serviss page
    "serviss.title": "Service",
    "serviss.subtitle": "Service maintenance of all installed equipment — technical inspection, adjustment and repair.",
    "serviss.section.title": "Service Maintenance",
    "serviss.p1": "MJ-Partneri provides service maintenance for all installed equipment, including technical inspection, adjustment and repair. Technical inspections are carried out according to manufacturer-recommended schedules and individually agreed maintenance plans with the client.",
    "serviss.p2": "Timely detection and elimination of potential defects is essential to extend equipment lifespan and ensure precise, economical and quality operation. Our specialists also provide consultations on equipment operation and the most suitable technical solutions according to your needs. Contact us and we will help find the best solution.",

    // Konsultacijas page
    "konsultacijas.title": "Consultation",
    "konsultacijas.subtitle": "Professional engineering consultations — the most suitable and effective technical solutions for your facility.",
    "konsultacijas.section.title": "Professional Consultations",
    "konsultacijas.p1": "MJ-Partneri offers professional consultations in the field of engineering communications, helping to choose the most suitable and effective technical solutions. We provide recommendations on the selection, design and installation of heating, ventilation, air conditioning and other systems, taking into account the specifics of the facility and client requirements.",
    "konsultacijas.p2": "Our specialists help optimize equipment selection, costs and energy efficiency, and also provide consultations on improving or modernizing existing systems. We ensure an individual approach to each project to achieve a safe, sustainable and economically justified solution. Contact us for a professional consultation.",

    // Kondicionieri page
    "kond.title": "Air Conditioners",
    "kond.subtitle": "Air conditioner supply, installation and consultation for apartments, private homes and offices.",
    "kond.metaDesc": "Air conditioner supply, installation and consultation. Split, multi split, designer and portable air conditioners from LG and Kaysun.",
    "kond.benefit.cooling": "Cooling & Heating",
    "kond.benefit.montage": "Installation & Consultation",
    "kond.benefit.home": "For apartments & homes",
    "kond.benefit.individual": "Individual offer",
    "kond.catalog": "E-Catalog",
    "kond.modelsAvailable": "models available",
    "kond.modelAvailable": "model available",
    "kond.filters": "Filters",
    "kond.sort": "Sort:",
    "kond.sortPopular": "Most popular",
    "kond.sortPriceAsc": "Price: lowest",
    "kond.sortPriceDesc": "Price: highest",
    "kond.sortArea": "By room area",
    "kond.noModels": "No matching models",
    "kond.clearFilters": "Clear filters",
    "kond.clearAllFilters": "Clear all filters",
    "kond.helpTitle": "Not sure which model to choose?",
    "kond.helpDesc": "Contact our specialists and we'll help find the ideal air conditioner for your needs and budget.",
    "kond.requestQuote": "Request Quote",
    "kond.getConsultation": "Get Consultation",
    "kond.usefulInfo": "Useful Information",
    "kond.montagePrice": "Installation calculated separately",
    "kond.cooling": "Cooling",
    "kond.heating": "Heating",
    "kond.filterClass": "Class",
    "kond.filterBrand": "Brand",
    "kond.filterArea": "Room area",
    "kond.filterType": "Type",
    "kond.filterFeatures": "Features",
    "kond.all": "All",
    "kond.area.upTo": "up to",
    "kond.roomArea": "Room area",
    "kond.mainFunctions": "Key features",
    "kond.price": "Price",

    // Kond categories
    "kond.cat.economy": "Economy Class",
    "kond.cat.standard": "Standard Class",
    "kond.cat.design": "Designer Class",
    "kond.cat.multi": "Multi Split Systems",
    "kond.cat.portable": "Portable Air Conditioners",

    // Kond chips
    "kond.chip.all": "All",
    "kond.chip.upTo25": "Up to 25 m²",
    "kond.chip.upTo35": "Up to 35 m²",
    "kond.chip.upTo50": "Up to 50 m²",
    "kond.chip.design": "Designer",
    "kond.chip.portable": "Portable",
    "kond.chip.withWifi": "With Wi-Fi",
    "kond.chip.withHeating": "With heating",

    // Kond type labels
    "kond.type.split": "Split",
    "kond.type.multi": "Multi split",
    "kond.type.portable": "Portable",
    "kond.type.design": "Designer",

    // Kond feature labels
    "kond.feat.selfDiag": "Self-diagnostics",
    "kond.feat.ionFilter": "Ion filter",
    "kond.feat.heating": "Heating",

    // Kond room size options
    "kond.room.upTo25": "up to 25 m²",
    "kond.room.upTo35": "up to 35 m²",
    "kond.room.upTo50": "up to 50 m²",
    "kond.room.upTo70": "up to 70 m²",

    // Kond accordion titles
    "kond.acc.apartment": "Air conditioners for apartments",
    "kond.acc.house": "Air conditioners for private homes",
    "kond.acc.commercial": "Air conditioners for commercial buildings & offices",
    "kond.acc.ventilation": "Air conditioning and ventilation",
    "kond.acc.portable": "Portable air conditioners",
    "kond.acc.lg": "LG air conditioners",
    "kond.acc.montage": "Consultation and installation",

    // Kond accordion content
    "kond.acc.apartment.p": "When choosing an air conditioner for an apartment, it's important to evaluate which room is the hottest, whether an outdoor unit can be placed on the facade, balcony or loggia, and what noise level and capacity is needed. If outdoor unit installation is not possible, a portable AC may be the solution, but for daily use, wall-mounted split-type units are usually more efficient, quieter and more convenient.",
    "kond.acc.house.p": "For private homes, it's important to evaluate not just price, but also room area, number of rooms, equipment placement and operating costs. For a single room, a split system is often sufficient, while multi split air conditioners — where multiple indoor units connect to one outdoor unit — are especially suitable for multiple rooms.",
    "kond.acc.commercial.p": "For offices, shops, cafes and other commercial spaces, we offer solutions that ensure fast and stable temperature control. Multi split systems are particularly suitable for multiple rooms, helping to reduce the number of outdoor units.",
    "kond.acc.ventilation.p": "An air conditioner provides cooling or heating, while a ventilation system provides fresh air supply and air exchange in rooms. For a complete indoor climate, the best solution is often a combination of air conditioning and ventilation systems.",
    "kond.acc.portable.p": "Portable air conditioners are suitable for situations where an outdoor unit cannot be installed or a temporary solution is needed. Keep in mind that these models are usually louder and less efficient than wall-mounted split systems.",
    "kond.acc.lg.p": "LG offers a wide range of air conditioners with Wi-Fi control, A++ energy efficiency and modern design. The Standard Plus series ensures reliable daily use, while the Artcool Mirror and Gallery series stand out with unique visual design that becomes an interior element.",
    "kond.acc.montage.intro": "Before choosing an air conditioner, we recommend consulting with our specialists. We will help evaluate:",
    "kond.acc.montage.1": "Room area and layout",
    "kond.acc.montage.2": "Installation options",
    "kond.acc.montage.3": "Required capacity",
    "kond.acc.montage.4": "Most suitable equipment type",
    "kond.acc.montage.5": "Budget and operating costs",
    "kond.acc.montage.footer": "Installation price is calculated separately after site inspection.",

    // Ventilacija page
    "vent.title": "Ventilation",
    "vent.subtitle": "Professional ventilation system design, installation and service for commercial, industrial and residential buildings throughout Latvia.",
    "vent.metaTitle": "Ventilation | MJ-Partneri",
    "vent.metaDesc": "Ventilation system design and installation for commercial, industrial and residential buildings. Heat recovery, mechanical ventilation and service.",
    "vent.s1.title": "Ventilation for commercial buildings",
    "vent.s1.p1": "Ventilation in commercial buildings is an essential prerequisite for comfort, health and hygiene compliance. As construction quality standards rise, so does the need for effective ventilation systems that ensure quality air exchange.",
    "vent.s1.p2": "MJ-Partneri provides ventilation system design and installation for commercial and industrial buildings, considering occupancy, room function and other key parameters. Our solutions allow regulation of air temperature, humidity and flow rate, ensuring optimal indoor climate in every room.",
    "vent.s2.title": "Ventilation for industrial buildings",
    "vent.s2.p1": "Ventilation in industrial facilities is particularly important due to elevated air pollution — smoke, dust, fumes and other harmful particles. MJ-Partneri has experience in ventilation system design and construction for industrial buildings, tailoring solutions to specific technological processes.",
    "vent.s2.p2": "Modern ventilation systems with heat recovery can significantly reduce operating costs and improve energy efficiency. We have completed projects in factories, wood processing, food production, warehouses and welding shops. Properly built ventilation ensures stable production processes and prevents downtime risks.",
    "vent.s3.title": "Ventilation for private homes",
    "vent.s3.p1": "Today, more and more clients choose ventilation for private homes, as modern, airtight buildings require controlled air exchange. A properly designed ventilation system ensures a healthy indoor climate and energy efficiency.",
    "vent.s3.p2": "MJ-Partneri offers individual solutions using heat recovery systems (heat exchangers) that provide fresh air supply while reducing heat loss. Properly built ventilation operates quietly and without drafts, ensuring comfort in all rooms.",
    "vent.s4.title": "Ventilation for apartments",
    "vent.s4.p1": "In many apartments, ventilation systems are outdated and insufficient, promoting moisture, mold and poor air quality. Opening windows provides only temporary relief and causes heat loss.",
    "vent.s4.p2": "Modern solutions allow installation of compact ventilation systems for apartments, providing continuous fresh air supply and improved indoor climate. These systems take up little space and effectively improve quality of life.",
    "vent.s5.title": "Ventilation systems",
    "vent.s5.p1": "A properly designed and installed ventilation system is one of the main indoor comfort factors. Insufficient ventilation causes elevated CO₂ levels, moisture and mold formation.",
    "vent.s5.p2": "MJ-Partneri offers both mechanical ventilation systems and improvement of existing systems. Mechanical ventilation provides controlled air exchange, filters the air and maintains optimal temperature and humidity throughout the year.",
    "vent.s5.p3": "We perform ventilation system design, installation and service — from apartments to large industrial and commercial buildings, providing the most suitable solution for each facility.",
    "vent.s1.alt": "Ventilation for commercial buildings",
    "vent.s2.alt": "Ventilation for industrial buildings",
    "vent.s3.alt": "Ventilation for private homes",
    "vent.s4.alt": "Ventilation for apartments",
    "vent.s5.alt": "Ventilation systems",

    // Admin (stays mostly LV since it's internal, but provide translations)
    "admin.title": "Project Management",
    "admin.changePassword": "Change Password",
    "admin.toSite": "To Website",
    "admin.logout": "Log Out",
    "admin.addProject": "Add Project",
    "admin.editProject": "Edit Project",
    "admin.newProject": "New Project",
    "admin.titleLabel": "Title *",
    "admin.titleEn": "Title (EN)",
    "admin.locationLabel": "Location *",
    "admin.locationEn": "Location (EN)",
    "admin.shortDesc": "Short Description *",
    "admin.shortDescEn": "Short Description (EN)",
    "admin.longDesc": "Full Description *",
    "admin.longDescEn": "Full Description (EN)",
    "admin.image": "Image",
    "admin.imageRequired": "Please add an image.",
    "admin.fillAll": "Please fill in all fields.",
    "admin.saved": "Saved!",
    "admin.savedDesc": "Project successfully added.",
    "admin.updated": "Updated!",
    "admin.updatedDesc": "Project successfully updated.",
    "admin.deleted": "Deleted!",
    "admin.deletedDesc": "Project successfully removed.",
    "admin.error": "Error",
    "admin.saveFailed": "Failed to save.",
    "admin.deleteFailed": "Failed to delete.",
    "admin.deleteConfirmTitle": "Delete project?",
    "admin.deleteConfirmDesc": "Are you sure you want to delete this project? This action cannot be undone.",
    "admin.cancel": "Cancel",
    "admin.delete": "Delete",
    "admin.save": "Save",
    "admin.saving": "Saving...",
    "admin.loading": "Loading...",
    "admin.newPassword": "New Password",
    "admin.confirmPassword": "Confirm Password",
    "admin.passwordMin": "At least 6 characters",
    "admin.passwordRepeat": "Repeat password",
    "admin.passwordShort": "Password must be at least 6 characters.",
    "admin.passwordMismatch": "Passwords do not match.",
    "admin.passwordChanged": "Success!",
    "admin.passwordChangedDesc": "Password changed.",
    "admin.imageKeep": "(leave empty to keep existing)",
    "admin.oneImage": "Only one image can be uploaded.",
  },
};

/* ═══════════════════════════════════════════════
   PROVIDER
   ═══════════════════════════════════════════════ */

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = localStorage.getItem("lang") as Lang | null;
    return saved === "en" ? "en" : "lv";
  });

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (newLang: Lang) => setLangState(newLang);

  const t = (key: string): string => {
    return translations[lang][key] || translations.lv[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
